import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import DetailHero from './components/DetailHero';
import ContentNav, { type TocItem } from './components/ContentNav';
import ConfirmSection from './components/ConfirmSection';
import AnalysisSection from './components/AnalysisSection';
import DisclaimerSection from './components/DisclaimerSection';
import { dramas } from '@/mocks/dramas';

const TOC_ITEMS: TocItem[] = [
  { id: 'confirm', label: '真实事件改编确认', index: '01' },
  { id: 'characters', label: '剧情原型人物对照', index: '02' },
  { id: 'score', label: '改编程度分析', index: '03' },
  { id: 'compare', label: '与真实爆料对比', index: '04' },
  { id: 'disclaimer', label: '片尾声明解读', index: '05' },
];

export default function DramaDetail() {
  const { id } = useParams<{ id: string }>();
  const drama = useMemo(() => dramas.find((d) => d.id === id), [id]);

  useEffect(() => {
    if (drama) {
      document.title = `《${drama.title}》真实事件改编确认与原型人物对照 - 剧真档案`;
    }
  }, [drama]);

  if (!drama) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 pt-16 md:pt-[72px] flex items-center justify-center px-4">
          <div className="text-center py-24">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-unknow-line text-2xl"></i>
            </span>
            <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">
              没有找到这份档案
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              该短剧可能尚未归档，或者链接地址有误。
            </p>
            <Link
              to="/dramas"
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回档案库
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const related = [
    ...dramas.filter((d) => d.category === drama.category && d.id !== drama.id),
    ...dramas.filter((d) => d.category !== drama.category && d.id !== drama.id),
  ].slice(0, 4);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: `《${drama.title}》真实事件改编确认与原型人物对照`,
        description: drama.summary,
        inLanguage: 'zh-CN',
        datePublished: drama.publishDate,
        dateModified: drama.publishDate,
        author: { '@type': 'Organization', name: drama.editor },
        publisher: { '@type': 'Organization', name: '剧真档案' },
        about: drama.tags.join('、'),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://aceasia.cn/' },
          {
            '@type': 'ListItem',
            position: 2,
            name: '短剧档案库',
            item: 'https://aceasia.cn/dramas',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: `《${drama.title}》`,
            item: `https://aceasia.cn/dramas/${drama.id}`,
          },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 pt-16 md:pt-[72px]">
        <div className="w-full px-4 md:px-8 py-5 border-b border-background-200">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '短剧档案库', to: '/dramas' },
              { label: `《${drama.title}》` },
            ]}
          />
        </div>

        <DetailHero drama={drama} />

        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <article className="lg:col-span-8 space-y-12">
              <ConfirmSection drama={drama} />
              <AnalysisSection drama={drama} />
              <DisclaimerSection drama={drama} />
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 space-y-5">
                <ContentNav items={TOC_ITEMS} />

                <div className="rounded-lg border border-background-200 bg-background-100 overflow-hidden">
                  <div className="px-4 py-3 border-b border-background-200 flex items-center gap-2">
                    <i className="ri-links-line text-primary-600"></i>
                    <h2 className="font-heading text-sm font-bold text-foreground-950">
                      同题材相关档案
                    </h2>
                  </div>
                  <ul>
                    {related.map((item) => (
                      <li key={item.id} className="border-b border-background-200 last:border-none">
                        <Link
                          to={`/dramas/${item.id}`}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-background-200/40 transition-colors"
                        >
                          <span className="w-11 h-14 shrink-0 rounded-md overflow-hidden bg-background-200">
                            <img
                              src={item.cover}
                              alt={`《${item.title}》封面缩略图`}
                              className="w-full h-full object-cover object-top"
                            />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-medium text-foreground-900 truncate">
                              《{item.title}》
                            </span>
                            <span className="mt-1 block text-[11px] text-foreground-500">
                              {item.category} · 还原度 {item.adaptationScore}%
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-5 rounded-lg bg-primary-950 text-background-100">
                  <h2 className="font-heading text-base font-bold text-background-50">
                    对这份档案有疑问？
                  </h2>
                  <p className="mt-2 text-sm text-background-200/80 leading-relaxed">
                    如果你手上有更完整的一手信息，欢迎提供线索，我们会重新核对并公开更新判定。
                  </p>
                  <Link
                    to="/about#contact"
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm hover:bg-accent-400 transition-colors whitespace-nowrap"
                  >
                    提供核对线索
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
    </div>
  );
}